import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AlertsProvider } from "./contexts/AlertsContext";
import { LogoImage } from "./components/layout/Logo";

// Layout components
import Layout from "./components/layout/Layout";
import PrivateRoute from "./components/auth/PrivateRoute";

// Page components
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ConfirmAccount from "./pages/ConfirmAccount";
import AcceptInvitation from "./pages/AcceptInvitation";
import Activities from "./pages/Activities";
import ActivityDetails from "./pages/ActivityDetails";
import Profile from "./pages/Profile";
import FamilySettings from "./pages/FamilySettings";
import NotFound from "./pages/NotFound";

// Demo components
import ColorPaletteDemo from "./components/ColorPaletteDemo";

const App: React.FC = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #174B67 0%, #2BB3A3 100%)",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <LogoImage height={120} />
          <Typography
            variant="h4"
            sx={{
              color: "white",
              fontWeight: 500,
              mb: 3,
              textShadow: "0 2px 4px rgba(0,0,0,0.3)",
            }}
          >
            CanYa
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255,255,255,0.9)",
              mb: 4,
              fontSize: "1.1rem",
            }}
          >
            Loading your family activities...
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress
              size={40}
              sx={{
                color: "white",
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <ThemeProvider>
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/confirm-account" element={<ConfirmAccount />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />

          {/* Protected routes */}
          <Route
            element={
              <PrivateRoute>
                <AlertsProvider>
                  <Layout />
                </AlertsProvider>
              </PrivateRoute>
            }
          >
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/activities" element={<Activities />} />
            <Route
              path="/activities/:activityId"
              element={<ActivityDetails />}
            />
            <Route path="/profile" element={<Profile />} />
            <Route path="/family-settings" element={<FamilySettings />} />
            <Route path="/theme-demo" element={<ColorPaletteDemo />} />
          </Route>

          {/* 404 route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Box>
    </ThemeProvider>
  );
};

export default App;
